import { fetchWithAuth } from '../helpers';
import { API_URL } from '../config/api';

// Helper function to handle response
const handleResponse = async (response: Response) => {
  const data = await response.json();
  if (!response.ok) {
    const error = (data && data.message) || response.statusText;
    return Promise.reject(error);
  }
  return data;
};

const documentService = {
  // Folder operations
  createFolder: async (data: { name: string; parentId?: number }) => {
    const response = await fetchWithAuth(`${API_URL}/api/documents/folders`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        "ngrok-skip-browser-warning": "69420"
      },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  getFolderContents: async (folderId: string | number) => {
    const response = await fetchWithAuth(
      `${API_URL}/api/documents/folders/${folderId}/contents`,
      {
        method: 'GET',
        headers: { "ngrok-skip-browser-warning": "69420" },
      }
    );
    return handleResponse(response);
  },

  updateFolder: async (id: number, data: { name: string }) => {
    const response = await fetchWithAuth(`${API_URL}/api/documents/folders/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        "ngrok-skip-browser-warning": "69420"
      },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  deleteFolder: async (id: number) => {
    const response = await fetchWithAuth(`${API_URL}/api/documents/folders/${id}`, {
      method: 'DELETE',
      headers: { "ngrok-skip-browser-warning": "69420" },
    });
    return handleResponse(response);
  },

  // File operations
  uploadFile: async (formData: FormData, onUploadProgress?: (progressEvent: any) => void) => {
    const axios = (await import('axios')).default;
    const token = localStorage.getItem('accessToken');
    
    if (!token) {
      throw new Error('Access Token Required');
    }
    
    const response = await axios.post(`${API_URL}/api/documents/files/upload`, formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        "ngrok-skip-browser-warning": "69420"
      },
      onUploadProgress,
    });
    return response.data;
  },

  updateFile: async (id: number, data: { name: string }) => {
    const response = await fetchWithAuth(`${API_URL}/api/documents/files/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        "ngrok-skip-browser-warning": "69420"
      },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  deleteFile: async (id: number) => {
    const response = await fetchWithAuth(`${API_URL}/api/documents/files/${id}`, {
      method: 'DELETE',
      headers: { "ngrok-skip-browser-warning": "69420" },
    });
    return handleResponse(response);
  },

  downloadFile: async (id: number) => {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_URL}/api/documents/files/${id}/download`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        "ngrok-skip-browser-warning": "69420"
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to download file');
    }
    
    return response.blob();
  },

  // Search
  searchDocuments: async (query: string) => {
    const response = await fetchWithAuth(
      `${API_URL}/api/documents/search?query=${encodeURIComponent(query)}`,
      {
        method: 'GET',
        headers: { "ngrok-skip-browser-warning": "69420" },
      }
    );
    return handleResponse(response);
  },

  // ── Trash ──────────────────────────────────────────────────────────────────
  getTrash: async () => {
    const response = await fetchWithAuth(`${API_URL}/api/documents/trash`, {
      method: 'GET',
      headers: { "ngrok-skip-browser-warning": "69420" },
    });
    return handleResponse(response);
  },

  restoreFile: async (id: number) => {
    const response = await fetchWithAuth(`${API_URL}/api/documents/trash/restore-file/${id}`, {
      method: 'PUT',
      headers: { "ngrok-skip-browser-warning": "69420" },
    });
    return handleResponse(response);
  },

  restoreFolder: async (id: number) => {
    const response = await fetchWithAuth(`${API_URL}/api/documents/trash/restore-folder/${id}`, {
      method: 'PUT',
      headers: { "ngrok-skip-browser-warning": "69420" },
    });
    return handleResponse(response);
  },

  permanentDeleteFile: async (id: number) => {
    const response = await fetchWithAuth(`${API_URL}/api/documents/trash/file/${id}`, {
      method: 'DELETE',
      headers: { "ngrok-skip-browser-warning": "69420" },
    });
    return handleResponse(response);
  },

  permanentDeleteFolder: async (id: number) => {
    const response = await fetchWithAuth(`${API_URL}/api/documents/trash/folder/${id}`, {
      method: 'DELETE',
      headers: { "ngrok-skip-browser-warning": "69420" },
    });
    return handleResponse(response);
  },

  emptyTrash: async () => {
    const response = await fetchWithAuth(`${API_URL}/api/documents/trash/empty`, {
      method: 'DELETE',
      headers: { "ngrok-skip-browser-warning": "69420" },
    });
    return handleResponse(response);
  },

  // Modification requests
  createModificationRequest: async (data: {
    requestType: 'delete' | 'edit';
    targetType: 'file' | 'folder';
    targetId: number;
    reason: string;
    newData?: any;
  }) => {
    const response = await fetchWithAuth(
      `${API_URL}/api/documents/modification-requests`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          "ngrok-skip-browser-warning": "69420"
        },
        body: JSON.stringify(data),
      }
    );
    return handleResponse(response);
  },

  getPendingRequests: async () => {
    const response = await fetchWithAuth(
      `${API_URL}/api/documents/modification-requests/pending`,
      {
        method: 'GET',
        headers: { "ngrok-skip-browser-warning": "69420" },
      }
    );
    return handleResponse(response);
  },

  reviewModificationRequest: async (
    id: number,
    data: { status: 'approved' | 'rejected'; reviewNote?: string }
  ) => {
    const response = await fetchWithAuth(
      `${API_URL}/api/documents/modification-requests/${id}/review`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          "ngrok-skip-browser-warning": "69420"
        },
        body: JSON.stringify(data),
      }
    );
    return handleResponse(response);
  },
};

export default documentService;
